import * as Yup from 'common/yup';
import {includes} from 'lodash';
import {FileTransferVerifyType} from 'types/FileTransferVerifyType';
import {TransportType} from 'types/TransportType';
import {AbstractServer, Server} from './AbstractServer';
import {CONTEXT_SEND_PARAMETER} from 'common/yup/constants';
import {TriggerOption} from 'types/transport/enums/TriggerOption';

export const transportTypeSchema = Yup.string().oneOf(TransportType.keys).defined();
export type ITransportType = Partial<keyof typeof TransportType.enum>; // Yup.InferType<typeof transportTypeSchema>;

export const fileTransferVerifyTypeSchema = Yup.string()
   .oneOf(FileTransferVerifyType.keys)
   .defined();

/**
 * Abstract Transport
 */
const _AbstractTransportCore = Yup.object({
   // typeName: transportTypeNameSchema.default(TransportTypeName.LOCAL),
   transportType: transportTypeSchema.default(TransportType.enum.FILE_SYSTEM),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.keys[0]),

   /**
    * True if files should be written using temporary names and
    * then renamed at the end of the transfer process.
    * Used by cloud, file system, ftp and smb transport types.
    */
   useTempFilenames: Yup.boolean(),
   /** Prefix to use with temp filenames. */
   tempPrefix: Yup.string(),
   /** Suffix to use with temp filenames. */
   tempSuffix: Yup.string(),

   /**
    * True (default) if file-locking should be used in jobs in which
    * this transport is used.
    * Used by file system transport type.
    */
   usesLocking: Yup.boolean(),

   usesTrigger: Yup.boolean(),
   triggerPattern: Yup.string().when('useTrigger', {
      is: true,
      then: Yup.string().test(
         'Trigger Pattern',
         "Trigger pattern must contain a '*' to match original file name, e.g. '*.TRG',\nor contain <FILENAME> or <EXT> variables.",
         (value) =>
            includes(value, '*') || includes(value, '<FILENAME>') || includes(value, '<EXT>')
      ),
   }),
   triggerTransfer: Yup.boolean(),
   triggerDelete: Yup.boolean(),

   usesQuiescence: Yup.bool(),
   quiescenceSeconds: Yup.number().default(30),

   /**
    * True (default) if an attempt should be made to create needed
    * directories when writing destination files.
    * Used by ftp and cloud storage transport types.
    */
   createDirs: Yup.boolean(),

   /**
    * Proxy server that should be used to communicate via this
    * transport.  Null (no proxy) by default.
    */
   proxyServerId: Yup.idString(),

   // server: AbstractServer.clone().optional().nullable(),
});
type IAbstractTransportCore = Yup.InferType<typeof _AbstractTransportCore>;
const AbstractTransportCore = _AbstractTransportCore as Yup.ObjectSchema<
   IAbstractTransportCore & {server?: Server}
>;

const AbstractTransportToSend = AbstractTransportCore.clone().transform(function (value, origin) {
   if (value._fileReadyCondition) {
      value.usesLocking = false;
      value.usesTrigger = false;
      value.usesQuiescence = false;

      if (value._fileReadyCondition === TriggerOption.enum.FileLocking) {
         value.usesLocking = true;
      } else if (value._fileReadyCondition === TriggerOption.enum.Name) {
         value.usesTrigger = true;
      } else if (value._fileReadyCondition === TriggerOption.enum.IDLE) {
         value.usesQuiescence = true;
      }

      delete value._fileReadyCondition;
      return value;
   }

   return value;
});

const AbstractTransportWithHelpFields = AbstractTransportCore.shape({
   _fileReadyCondition: Yup.string().oneOf(TriggerOption.keys),
}).transform(function (value, origin) {
   if (value._fileReadyCondition === undefined) {
      if (origin.usesLocking) {
         value._fileReadyCondition = TriggerOption.enum.FileLocking;
      } else if (origin.usesTrigger) {
         value._fileReadyCondition = TriggerOption.enum.Name;
      } else if (origin.usesQuiescence) {
         value._fileReadyCondition = TriggerOption.enum.IDLE;
      } else {
         value._fileReadyCondition = TriggerOption.enum.None;
      }
   }

   return value;
});

export const AbstractTransport = AbstractTransportCore.when(CONTEXT_SEND_PARAMETER, {
   is: true,
   then: AbstractTransportToSend,
   otherwise: AbstractTransportWithHelpFields,
}) as typeof AbstractTransportWithHelpFields;

export type Transport = Yup.InferType<typeof AbstractTransportWithHelpFields>;
